/**
 * Product care browser.
 * Search + category chips over a product grid. Clicking a card opens an inline
 * panel with cleaning, assembly and specifications.
 *
 * Content: assets/product-care-data.json
 * Photos, titles and links: the Products list on the section (resolved in Liquid).
 */

if (!customElements.get('product-care')) {
  customElements.define(
    'product-care',
    class ProductCare extends HTMLElement {
      constructor() {
        super();
        this.data = null;
        this.products = [];
        this.category = 'all';
        this.query = '';
        this.openId = null;
        this.tab = 'cleaning';
        this.lang = window.Shopify && window.Shopify.locale ? window.Shopify.locale : 'en';
      }

      /* ── helpers ── */

      /** Resolve a localised value: plain string, or {en: "", nl: "", de: ""}. */
      i(val) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
          return val[this.lang] || val.en || Object.values(val)[0] || '';
        }
        return val || '';
      }

      esc(str) {
        return String(str == null ? '' : str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      parseAttr(name, fallback) {
        try {
          return JSON.parse(this.dataset[name] || '');
        } catch (e) {
          /* Silent failure here means every product loses its photo and title, so say so. */
          console.error('[product-care] Could not parse data-' + name + ':', e.message, this.dataset[name]);
          return fallback;
        }
      }

      /* ── lifecycle ── */

      connectedCallback() {
        this.shop = this.parseAttr('products', {});
        this.collections = this.parseAttr('collections', []);
        this.t = this.parseAttr('labels', {});

        this.els = {
          grid: this.querySelector('.product-care__grid'),
          chips: this.querySelector('.product-care__chips'),
          search: this.querySelector('.product-care__search'),
          count: this.querySelector('.product-care__count'),
          empty: this.querySelector('.product-care__no-results')
        };

        if (this.els.search) {
          this.els.search.addEventListener('input', () => {
            this.query = this.els.search.value.toLowerCase().trim();
            this.openId = null;
            this.render();
          });
        }

        this.addEventListener('click', (e) => this.onClick(e));
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.openId) this.close();
        });

        /*
         * Only width matters here, since the panel is repositioned by column
         * count. Scrolling on a phone hides the address bar, which fires resize
         * with a new height, and re-rendering on that replayed the open
         * animation mid-scroll.
         */
        this._lastWidth = window.innerWidth;
        this._onResize = this.debounce(() => {
          if (window.innerWidth === this._lastWidth) return;
          this._lastWidth = window.innerWidth;
          if (this.openId) this.render();
        }, 150);
        window.addEventListener('resize', this._onResize);

        this.load();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this._onResize);
      }

      debounce(fn, wait) {
        let timer;
        return function () {
          clearTimeout(timer);
          timer = setTimeout(fn, wait);
        };
      }

      load() {
        fetch(this.dataset.careUrl)
          .then((r) => {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          })
          .then((data) => {
            this.data = data;
            this.products = (data.products || []).filter((p) => p && p.id);
            this.renderChips();
            this.render();
            this.openFromHash();
          })
          .catch(() => {
            this.els.grid.innerHTML = '';
            if (this.els.empty) this.els.empty.hidden = false;
          });
      }

      /**
       * The Shopify product id is the canonical key in the URL. It is the only
       * identifier a product page can know, since Liquid cannot read this JSON,
       * and it is the same in every language. The internal id is still accepted
       * so older shared links keep working.
       */
      hashKeyFor(product) {
        return String(product.product_id || product.id);
      }

      openFromHash() {
        const m = /^#care-(.+)$/.exec(window.location.hash || '');
        if (!m) return;
        const key = decodeURIComponent(m[1]);
        const found = this.products.find((p) => String(p.product_id) === key || p.id === key);
        if (!found) return;
        this.openId = found.id;
        this.render();
        const panel = this.querySelector('.product-care__panel');
        if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      /* ── data shaping ── */

      /**
       * Match a JSON row to a Shopify product. product_id is preferred because it
       * is identical in every language; handles are translated per locale and so
       * stop matching on the Dutch and German storefronts.
       */
      shopFor(product) {
        if (product.product_id && this.shop[product.product_id]) {
          return this.shop[product.product_id];
        }
        if (!this._byHandle) {
          this._byHandle = {};
          Object.keys(this.shop).forEach((id) => {
            const entry = this.shop[id];
            if (entry && entry.handle) this._byHandle[entry.handle] = entry;
          });
        }
        return this._byHandle[product.product_handle] || null;
      }

      /** The collections a product belongs to, as resolved by Liquid. */
      collectionsFor(product) {
        const shop = this.shopFor(product);
        return (shop && shop.collections) || [];
      }

      /** Label under the card: the first filter collection this product is in. */
      categoryName(product) {
        const handles = this.collectionsFor(product);
        const hit = this.collections.find((c) => handles.indexOf(c.handle) !== -1);
        return hit ? hit.title : '';
      }

      titleFor(product) {
        const name = this.i(product.name);
        if (name) return name;
        const shop = this.shopFor(product);
        if (shop && shop.title) return shop.title;

        /* No Shopify match. Warn once, then show something readable rather than a handle. */
        if (!this._warned) this._warned = {};
        if (!this._warned[product.id]) {
          this._warned[product.id] = true;
          console.warn(
            '[product-care] No Shopify product found for handle "' +
              product.product_handle +
              '". Add it to the section\'s Products list, and check it is published to this market and that its URL handle is not translated.'
          );
        }
        return product.id.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());
      }

      haystack(product) {
        const handles = this.collectionsFor(product);
        const titles = this.collections.filter((c) => handles.indexOf(c.handle) !== -1).map((c) => c.title);
        const parts = [this.titleFor(product)].concat(titles);
        const push = (v) => parts.push(this.i(v));

        ((product.cleaning || {}).steps || []).forEach(push);
        ((product.assembly || {}).steps || []).forEach(push);
        [(product.cleaning || {}).tip, (product.assembly || {}).tip, product.good_to_know].forEach((tip) => {
          if (tip) {
            push(tip.title);
            push(tip.text);
          }
        });
        (product.specifications || []).forEach((spec) => {
          push(spec.name);
          push(spec.value);
        });
        return parts.join(' ').toLowerCase();
      }

      matches(product) {
        if (this.category !== 'all' && this.collectionsFor(product).indexOf(this.category) === -1) return false;
        if (!this.query) return true;
        return this.haystack(product).indexOf(this.query) !== -1;
      }

      /* ── events ── */

      onClick(e) {
        const chip = e.target.closest('.product-care__chip');
        if (chip) {
          this.category = chip.dataset.category;
          this.openId = null;
          this.renderChips();
          this.render();
          return;
        }

        if (e.target.closest('.product-care__close')) {
          this.close();
          return;
        }

        const tab = e.target.closest('.product-care__tab');
        if (tab) {
          this.switchTab(tab.dataset.tab);
          return;
        }

        /* Let the product link through untouched. */
        if (e.target.closest('.product-care__panel-link')) return;

        const card = e.target.closest('.product-care__card');
        if (card) {
          const id = card.dataset.id;
          this.openId = this.openId === id ? null : id;
          this.tab = 'cleaning';
          this.render();
          if (this.openId) {
            const opened = this.products.find((p) => p.id === this.openId);
            this.setHash('#care-' + encodeURIComponent(this.hashKeyFor(opened)));
            const panel = this.querySelector('.product-care__panel');
            if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } else {
            this.setHash('');
          }
        }
      }

      /**
       * Swap the open tab without rebuilding the panel. A full re-render replayed
       * the open animation, so the whole box flashed on every tab click.
       */
      switchTab(next) {
        this.tab = next;
        const panel = this.querySelector('.product-care__panel');
        const product = this.products.find((p) => p.id === this.openId);
        if (!panel || !product) {
          this.render();
          return;
        }

        panel.querySelectorAll('.product-care__tab').forEach((button) => {
          const on = button.dataset.tab === next;
          button.classList.toggle('is-active', on);
          button.setAttribute('aria-selected', on);
        });

        const pane = panel.querySelector('.product-care__pane');
        if (pane) pane.innerHTML = this.paneHTML(product);
      }

      close() {
        const card = this.querySelector('.product-care__card[data-id="' + this.openId + '"]');
        this.openId = null;
        this.setHash('');
        this.render();
        if (card) card.focus();
      }

      setHash(hash) {
        if (!window.history || !window.history.replaceState) return;
        const url = window.location.pathname + window.location.search + hash;
        window.history.replaceState(null, '', url);
      }

      /* ── rendering ── */

      renderChips() {
        if (!this.els.chips) return;

        /* Only offer a chip if at least one product on this page is in it. */
        const used = {};
        this.products.forEach((p) => {
          this.collectionsFor(p).forEach((h) => {
            used[h] = true;
          });
        });
        const cats = this.collections.filter((c) => used[c.handle]);
        if (!cats.length) {
          this.els.chips.innerHTML = '';
          return;
        }

        let html =
          '<button type="button" class="product-care__chip' +
          (this.category === 'all' ? ' is-active' : '') +
          '" data-category="all" aria-pressed="' +
          (this.category === 'all') +
          '">' +
          this.esc(this.t.all || 'All') +
          '</button>';

        cats.forEach((c) => {
          const on = this.category === c.handle;
          html +=
            '<button type="button" class="product-care__chip' +
            (on ? ' is-active' : '') +
            '" data-category="' +
            this.esc(c.handle) +
            '" aria-pressed="' +
            on +
            '">' +
            this.esc(c.title) +
            '</button>';
        });

        this.els.chips.innerHTML = html;
      }

      columnCount() {
        const cols = window.getComputedStyle(this.els.grid).gridTemplateColumns;
        if (!cols || cols === 'none') return 1;
        return Math.max(1, cols.split(' ').filter(Boolean).length);
      }

      render() {
        if (!this.data) return;
        const list = this.products.filter((p) => this.matches(p));

        if (this.els.count) {
          const tpl = list.length === 1 ? this.t.results_one || '1 product' : this.t.results_many || '{count} products';
          this.els.count.textContent = list.length ? tpl.replace('{count}', list.length) : '';
        }
        if (this.els.empty) this.els.empty.hidden = list.length > 0;

        this.els.grid.innerHTML = list.map((p) => this.cardHTML(p)).join('');

        if (!this.openId) return;

        const index = list.findIndex((p) => p.id === this.openId);
        if (index === -1) {
          this.openId = null;
          return;
        }

        /* Drop the panel at the end of the row the open card sits in. */
        const cols = this.columnCount();
        const endOfRow = Math.min(list.length - 1, index - (index % cols) + cols - 1);
        const cards = this.els.grid.children;
        const holder = document.createElement('div');
        holder.innerHTML = this.panelHTML(list[index]);
        cards[endOfRow].after(holder.firstElementChild);
      }

      cardHTML(product) {
        const shop = this.shopFor(product);
        const title = this.titleFor(product);
        const cat = this.categoryName(product);
        const open = this.openId === product.id;

        const media = shop && shop.image
          ? '<img class="product-care__card-img" src="' +
            this.esc(shop.image) +
            '" alt="' +
            this.esc(shop.alt || title) +
            '" loading="lazy" width="500" height="500">'
          : '<span class="product-care__card-placeholder" aria-hidden="true"></span>';

        return (
          '<button type="button" class="product-care__card' +
          (open ? ' is-open' : '') +
          '" data-id="' +
          this.esc(product.id) +
          '" aria-expanded="' +
          open +
          '">' +
          '<span class="product-care__card-media">' +
          media +
          '</span>' +
          '<span class="product-care__card-body">' +
          '<span class="product-care__card-title">' +
          this.esc(title) +
          '</span>' +
          (cat ? '<span class="product-care__card-cat">' + this.esc(cat) + '</span>' : '') +
          '</span>' +
          '</button>'
        );
      }

      tipHTML(tip, modifier) {
        if (!tip) return '';
        const title = this.i(tip.title);
        const text = this.i(tip.text);
        if (!title && !text) return '';
        return (
          '<div class="product-care__tip' + (modifier ? ' ' + modifier : '') + '">' +
          (title ? '<p class="product-care__tip-title">' + this.esc(title) + '</p>' : '') +
          (text ? '<p class="product-care__tip-text">' + this.esc(text) + '</p>' : '') +
          '</div>'
        );
      }

      /** Tabs that have content for this product. Empty sections are dropped. */
      tabsFor(product) {
        const tabs = [];
        const hasSteps = (group) => group && Array.isArray(group.steps) && group.steps.some((s) => this.i(s));

        if (hasSteps(product.cleaning)) tabs.push(['cleaning', this.t.tab_cleaning || 'Cleaning & care']);
        if (hasSteps(product.assembly)) tabs.push(['assembly', this.t.tab_assembly || 'Assembly & fitting']);
        /* Safety sits before specs: it matters more, and on a phone the last tab
           starts off screen. */
        if (hasSteps(product.safety)) tabs.push(['safety', this.t.tab_safety || 'Safety']);
        if ((product.specifications || []).some((spec) => this.i(spec.value))) {
          tabs.push(['specifications', this.t.tab_specifications || 'Specifications']);
        }
        return tabs;
      }

      panelHTML(product) {
        const shop = this.shopFor(product);
        const title = this.titleFor(product);
        const cat = this.categoryName(product);
        const cleaning = product.cleaning || {};
        const assembly = product.assembly || {};
        const specs = product.specifications || [];
        const gtk = product.good_to_know;

        const media = shop && shop.image
          ? '<img src="' + this.esc(shop.image) + '" alt="' + this.esc(shop.alt || title) + '" width="500" height="500">'
          : '<span class="product-care__card-placeholder" aria-hidden="true"></span>';

        /* Only show a tab when that product actually has content for it. */
        const tabs = this.tabsFor(product);
        if (!tabs.some((pair) => pair[0] === this.tab)) {
          this.tab = tabs.length ? tabs[0][0] : 'cleaning';
        }

        let html =
          '<div class="product-care__panel" id="care-panel-' +
          this.esc(product.id) +
          '">' +
          '<div class="product-care__panel-head">' +
          '<span class="product-care__panel-media">' +
          media +
          '</span>' +
          '<div class="product-care__panel-intro">' +
          (cat ? '<p class="product-care__panel-cat">' + this.esc(cat) + '</p>' : '') +
          '<h3 class="product-care__panel-title">' +
          this.esc(title) +
          '</h3>' +
          (shop && shop.url
            ? '<a class="product-care__panel-link link" href="' +
              this.esc(shop.url) +
              '">' +
              this.esc(this.t.view_product || 'View product') +
              '</a>'
            : '') +
          '</div>' +
          '<button type="button" class="product-care__close" aria-label="' +
          this.esc(this.t.close || 'Close') +
          '" aria-controls="care-panel-' +
          this.esc(product.id) +
          '">' +
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>' +
          '</button>' +
          '</div>';

        html += this.tipHTML(gtk, 'product-care__tip--good-to-know');

        /* A single tab needs no tab bar, the heading says it all. */
        if (tabs.length > 1) {
          html += '<div class="product-care__tabs" role="tablist">';
          tabs.forEach((pair) => {
            const on = this.tab === pair[0];
            html +=
              '<button type="button" class="product-care__tab' +
              (on ? ' is-active' : '') +
              '" data-tab="' +
              pair[0] +
              '" role="tab" aria-selected="' +
              on +
              '">' +
              this.esc(pair[1]) +
              '</button>';
          });
          html += '</div>';
        } else if (tabs.length === 1) {
          html += '<p class="product-care__pane-heading">' + this.esc(tabs[0][1]) + '</p>';
        }

        if (!tabs.length) return html + '</div>';

        html += '<div class="product-care__pane" role="tabpanel">' + this.paneHTML(product) + '</div></div>';
        return html;
      }

      /** Just the contents of the open tab, so a tab switch can swap this alone. */
      paneHTML(product) {
        if (this.tab === 'cleaning') {
          const steps = (product.cleaning || {}).steps || [];
          return (
            '<ul class="product-care__list product-care__list--bullets">' +
            steps.map((s) => '<li>' + this.esc(this.i(s)) + '</li>').join('') +
            '</ul>' +
            this.tipHTML((product.cleaning || {}).tip)
          );
        }
        if (this.tab === 'assembly') {
          const steps = (product.assembly || {}).steps || [];
          return (
            '<ol class="product-care__list product-care__list--steps">' +
            steps.map((s) => '<li>' + this.esc(this.i(s)) + '</li>').join('') +
            '</ol>' +
            this.tipHTML((product.assembly || {}).tip)
          );
        }
        if (this.tab === 'safety') {
          const steps = (product.safety || {}).steps || [];
          return (
            '<ul class="product-care__list product-care__list--bullets">' +
            steps.map((s) => '<li>' + this.esc(this.i(s)) + '</li>').join('') +
            '</ul>' +
            this.tipHTML((product.safety || {}).tip)
          );
        }
        return (
          '<table class="product-care__specs"><tbody>' +
          (product.specifications || [])
            .map(
              (spec) =>
                '<tr><th scope="row">' +
                this.esc(this.i(spec.name)) +
                '</th><td>' +
                this.esc(this.i(spec.value)) +
                '</td></tr>'
            )
            .join('') +
          '</tbody></table>'
        );
      }
    }
  );
}
